import { useState, useMemo } from 'react'
import { useSetRecoilState, useRecoilValue, useRecoilState } from 'recoil'
import {
  selectedProductsSelector,
  orderAtom,
  selectedProductsAtom,
} from 'atoms'
import { PrintPackageHeader } from './'
import { CardSelector, SearchBar } from 'components'
import Flex from 'components/priority/Flex/Flex'
import Text from 'components/priority/Text/Text'
import { useErrorHandler } from 'hooks/utility/useErrorHandler'
import { DIGNITY_DIRECT } from '@messenger/consts'
import { useTranslation } from 'react-i18next'
import { getItemDisplayName } from 'utils/global/getItemDisplayName'
import { getOrderProducts as getOrderProductsApi } from '@/api/orderProducts'

const M3_PLACEHOLDER_IMAGE =
  'https://s3-us-west-2.amazonaws.com/bassmollett-products/M3.png'

const normalizeForMdlPicker = (product) =>
  product.source === 'm3'
    ? { ...product, image: M3_PLACEHOLDER_IMAGE, description: undefined }
    : product

export const Products = ({ caseInfo, categories, products }) => {
  const { t } = useTranslation()
  const handleError = useErrorHandler('Products')
  const [search, setSearch] = useState('')
  const [selectedProducts, setSelectedProducts] = useRecoilState(
    selectedProductsSelector,
  )
  const order = useRecoilValue(orderAtom)
  const setOrderProducts = useSetRecoilState(selectedProductsAtom)

  // Parse hidden products from environment variable
  const hiddenProducts = useMemo(() => {
    try {
      return process.env.REACT_APP_HIDDEN_PRODUCTS
        ? (process.env.REACT_APP_HIDDEN_PRODUCTS?.split(',') ?? [])
        : []
    } catch (error) {
      console.error('Error parsing REACT_APP_HIDDEN_PRODUCTS:', error)
      return []
    }
  }, [])

  const filteredCategories = useMemo(() => {
    const workingCategories = { ...categories }

    // Initialize "dignity direct" category if it doesn't exist
    if (!workingCategories[DIGNITY_DIRECT]) {
      workingCategories[DIGNITY_DIRECT] = []
    }

    // Create filtered categories object
    const filteredCategories = {}

    if (search.trim() === '') {
      Object.keys(workingCategories).forEach((category) => {
        let filtered = []

        // Special handling for "dignity direct" category
        if (category === DIGNITY_DIRECT) {
          filtered = products.filter(
            (product) =>
              product.archived === false &&
              !hiddenProducts.includes(product.id),
          )
        } else {
          // Regular filtering for other categories
          filtered = products.filter((product) => {
            const exists = workingCategories[category].findIndex(
              (obj) => obj.title === product.name,
            )
            return exists > -1
          })
        }

        filteredCategories[category] = filtered
      })

      return filteredCategories
    } else {
      // Search functionality
      const escape = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      const searchRegex = new RegExp(escape(search), 'i')

      Object.keys(workingCategories).forEach((category) => {
        let filtered = []

        // Special handling for "dignity direct" category with search
        if (category === DIGNITY_DIRECT) {
          filtered = products.filter(
            (product) =>
              product.archived === false &&
              !hiddenProducts.includes(product.id),
          )
        } else {
          // Regular filtering for other categories
          filtered = products.filter((product) => {
            const exists = workingCategories[category].findIndex(
              (obj) => obj.title === product.name,
            )
            return exists > -1
          })
        }

        // Apply search filter to all categories
        filteredCategories[category] = filtered.filter((product) => {
          const displayName = getItemDisplayName(product.name, 'products', t)
          return searchRegex.test(displayName)
        })
      })

      return filteredCategories
    }
  }, [categories, products, search, hiddenProducts, t])

  const categoryAmounts = useMemo(() => {
    const categoryObject = {}
    Object.keys(filteredCategories).forEach((category) => {
      categoryObject[category] = selectedProducts.filter((selectedProduct) =>
        filteredCategories[category].some(
          (product) =>
            product.id === selectedProduct.product_id &&
            selectedProduct.selected,
        ),
      ).length
    })
    return categoryObject
  }, [selectedProducts, filteredCategories])

  const getOrderProducts = async () => {
    try {
      const { data: orderProductsData } = await getOrderProductsApi(order.id)
      setOrderProducts(orderProductsData)
    } catch (error) {
      handleError(error?.response?.data?.message || error.message, error)
    }
  }

  return (
    <>
      <PrintPackageHeader
        title={t('package.navigationSteps.chooseProducts')}
        caseInfo={caseInfo || ''}
      />
      <SearchBar
        placeholder={t('package.searchPlaceholder')}
        value={[search, setSearch]}
        margin="32px 40px"
        width="600px"
      />
      {Object.keys(filteredCategories).map((category, idx) => (
        <Flex column width="100%" margin="0 40px" key={`category-${idx}`}>
          {filteredCategories[category].length > 0 && (
            <>
              <Flex align="center">
                <Text size="16px" weight="bold">
                  {category} •{' '}
                </Text>
                <Text size="14px">
                  {categoryAmounts[category]} {t('package.selected')}
                </Text>
              </Flex>
              <CardSelector
                padding="16px 0 40px"
                cardAlign="flex-start"
                cardJustify="flex-start"
                cards={filteredCategories[category].map(normalizeForMdlPicker)}
                cardWidth="212px"
                cardHeight="220px"
                cardPadding="24px 56px"
                selectedState={[selectedProducts, setSelectedProducts]}
                allowMultiple
                matchProperty="product_id"
                selectedStateUpdate={getOrderProducts}
                type="products"
              />
            </>
          )}
        </Flex>
      ))}
    </>
  )
}

export default Products
